import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress, Box, Grid, Typography, IconButton, Snackbar, Alert, FormControlLabel, Switch, Checkbox, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import { useSalonsListQuery, useSalonsCategoriesListQuery, useServicesCreateMutation, useAuthProfileRetrieveQuery } from '../../../../services/GLOWClient';

import theme from '../../../../theme';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import SpaIcon from '@mui/icons-material/Spa';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TranslateIcon from '@mui/icons-material/Translate';
import SaveIcon from '@mui/icons-material/Save';
import { parseDurationString, formatDuration } from '../../../../utils/formatters';
import TranslationValidationModal from '../TranslationValidationModal';
import { IconContainer, DialogTitleText, SubTitleScreen, PrimaryButton, SecondaryButton } from '../../../../SystemDesign/styles';
import ServiceConfirmationModeField from './ServiceConfirmationModeField';

const AddService = ({ open, onClose, onSuccess, salonId = null, selectedSalonId = null, isCreationMode = false, categories = [], preselectedCategory = null, nested = false, sx, ...otherProps }) => {
    const { t, i18n } = useTranslation();
    const currentSalonId = salonId || selectedSalonId;

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        salon: currentSalonId || '',
        category: preselectedCategory?.category_id || preselectedCategory?.id || preselectedCategory?.pk || '',
        price: '',
        prix_fixe: true,
        client_booking_disabled: false,
        booking_confirmation_mode: 'automatic',
        duration: '',
        durationDisplay: '',
        image: null,
        primaryImageIndex: 0
    });

    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [primaryImageIndex, setPrimaryImageIndex] = useState(0);
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const singleImageInputRef = useRef(null);
    const multipleImagesInputRef = useRef(null);

    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    const { data: salonsData, isLoading: salonsLoading, error: salonsError } = useSalonsListQuery(
        { ...(userProfile?.id && { owner: userProfile.id }) },
        { skip: !userProfile?.id }
    );

    const activeSalonId = formData.salon || currentSalonId;
    const { data: categoriesData, isLoading: categoriesLoading } = useSalonsCategoriesListQuery(
        activeSalonId ? { salonId: Number(activeSalonId), language: i18n.language } : { salonId: 0 },
        { skip: !activeSalonId }
    );

    const allCategories = useMemo(() => {
        const existingCategories = Array.isArray(categoriesData) ? categoriesData : [];
        const newCategories = Array.isArray(categories) ? categories : [];

        const filteredNewCategories = newCategories.filter(cat => {
            const catSalonId = cat.salon || cat.salon_id;
            return Number(catSalonId) === Number(activeSalonId);
        });

        const combined = [...existingCategories];
        filteredNewCategories.forEach(newCat => {
            const newCatId = newCat.category_id || newCat.id || newCat.pk;
            const exists = combined.some(existingCat => {
                const existingCatId = existingCat.category_id || existingCat.id || existingCat.pk;
                return existingCatId === newCatId;
            });
            if (!exists) combined.push(newCat);
        });

        return combined;
    }, [categoriesData, categories, activeSalonId]);

    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();

    const getTranslatedField = (item, field) => {
        const translationsKey = `${field}_translations`;
        const localizedKey = `localized_${field}`;
        const translations = item?.[translationsKey] || {};

        if (translations[currentLanguage]) return translations[currentLanguage];
        if (item?.[localizedKey]) return item[localizedKey];

        for (const fallbackLanguage of ['fr', 'en', 'ar']) {
            if (translations[fallbackLanguage]) return translations[fallbackLanguage];
        }

        return item?.[field] || '';
    };

    const [createService, { isLoading: isCreating }] = useServicesCreateMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Translation modal states
    const [translationModalOpen, setTranslationModalOpen] = useState(false);
    const [translationModalLoading, setTranslationModalLoading] = useState(false);
    const [pendingTranslations, setPendingTranslations] = useState(null);
    const [createdServiceId, setCreatedServiceId] = useState(null);
    const [createdServiceDraft, setCreatedServiceDraft] = useState({ name: '', description: '' });
    const [pendingCreatedService, setPendingCreatedService] = useState(null);
    const pollIntervalRef = useRef(null);
    const uploadInputId = useMemo(() => `service-multiple-images-upload-${Math.random().toString(36).slice(2)}`, []);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            // Dialog just opened: reset once
            wasOpenRef.current = true;
            setFormData({
                name: '',
                description: '',
                salon: currentSalonId || '',
                category: preselectedCategory?.category_id || preselectedCategory?.id || preselectedCategory?.pk || '',
                price: '',
                prix_fixe: true,
                client_booking_disabled: false,
                booking_confirmation_mode: 'automatic',
                duration: '',
                durationDisplay: '',
                image: null,
                primaryImageIndex: 0
            });
            setImages([]);
            setImagePreviews([]);
            setErrors({});
            setPrimaryImageIndex(0);

            if (singleImageInputRef.current) singleImageInputRef.current.value = '';
            if (multipleImagesInputRef.current) multipleImagesInputRef.current.value = '';
        }
        if (!open && wasOpenRef.current) {
            // Dialog just closed
            wasOpenRef.current = false;
            setDialogClosed(true);
        }
    }, [open]);

    // When props change while open, update only the relevant fields without wiping images/previews
    useEffect(() => {
        if (!open) return;
        if (currentSalonId) {
            setFormData(prev => ({ ...prev, salon: currentSalonId }));
        }
    }, [currentSalonId, open]);

    useEffect(() => {
        if (!open) return;
        if (preselectedCategory) {
            const categoryId = preselectedCategory.category_id || preselectedCategory.id || preselectedCategory.pk;
            setFormData(prev => ({ ...prev, category: categoryId }));
        }
    }, [preselectedCategory, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'durationDisplay') {
            const durationInMinutes = parseDurationString(value);
            setFormData(prev => ({
                ...prev,
                durationDisplay: value,
                duration: durationInMinutes
            }));
        } else if (name === 'salon') {
            const numericValue = value === '' ? '' : Number(value);
            setFormData(prev => ({
                ...prev,
                [name]: numericValue,
                category: ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name] || (name === 'durationDisplay' && errors.duration)) {
            setErrors(prev => ({
                ...prev,
                [name === 'durationDisplay' ? 'duration' : name]: ''
            }));
        }
    };

    const handleMultipleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        console.log('Multiple images selected:', files.length, 'files:', files.map(f => f.name));

        if (files.length > 0) {
            setImages(prevImages => [...prevImages, ...files]);

            Promise.all(files.map(file => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    console.log(`Preview data for image ${files.indexOf(file) + 1}:`, reader.result.substring(0, 50) + '...');
                    resolve(reader.result);
                };
                reader.onerror = (error) => {
                    console.error(`Error reading image ${files.indexOf(file) + 1}:`, error);
                    reject(error);
                };
                reader.readAsDataURL(file);
            })))
            .then((newPreviews) => {
                console.log('All previews created successfully:', newPreviews.length);
                setImagePreviews(prev => {
                    const updated = [...prev, ...newPreviews];
                    if (prev.length === 0 && updated.length > 0) setPrimaryImageIndex(0);
                    return updated;
                });
            })
            .catch((error) => {
                console.error('Error reading image files:', error);
                setErrors(prev => ({
                    ...prev,
                    images: t('pro_service.add_service.image_load_error')
                }));
            })
            .finally(() => {
                if (multipleImagesInputRef.current) {
                    multipleImagesInputRef.current.value = '';
                }
            });

            if (errors.images) {
                setErrors(prev => ({
                    ...prev,
                    images: ''
                }));
            }
        } else {
            console.log('No files selected');
            if (multipleImagesInputRef.current) {
                multipleImagesInputRef.current.value = '';
            }
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));

        setPrimaryImageIndex(prev => {
            if (index === prev) {
                return imagePreviews.length > 1 ? 0 : -1;
            } else if (index < prev) {
                return prev - 1;
            }
            return prev;
        });
    };

    const setPrimaryImage = (index) => {
        if (index >= 0 && index < imagePreviews.length) {
            setPrimaryImageIndex(index);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name?.trim()) newErrors.name = t('pro_service.add_service.name_required_error');
        if (!formData.salon && !currentSalonId) newErrors.salon = t('pro_service.add_service.salon_required_error');
        if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
            newErrors.price = t('pro_service.add_service.price_valid_error');
        }
        if (!formData.duration || isNaN(formData.duration) || parseInt(formData.duration) <= 0) {
            newErrors.duration = t('pro_service.add_service.duration_valid_error');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveTranslations = async (editedTranslations) => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        if (!createdServiceId) return;
        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            await fetch(`/api/services/${createdServiceId}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name_translations: editedTranslations.name,
                    description_translations: editedTranslations.description,
                    translations_auto: false,
                }),
            });
        } catch (e) {
            console.warn('[service translations] patch error', e);
        }
        setSnackbar({ open: true, message: t('pro_service.add_service.service_created_success'), severity: 'success' });
        if (onSuccess && pendingCreatedService) onSuccess(pendingCreatedService);
        if (onClose) onClose();
    };

    const handleTranslationSkip = () => {
        setTranslationModalOpen(false);
        clearInterval(pollIntervalRef.current);
        setSnackbar({ open: true, message: t('pro_service.add_service.service_created_success'), severity: 'success' });
        if (onSuccess && pendingCreatedService) onSuccess(pendingCreatedService);
        if (onClose) onClose();
    };

    const handleSubmit = async (withTranslationCheck = false) => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const salonIdToUse = formData.salon || currentSalonId;

            if (!formData.name?.trim()) {
                setErrors({ name: t('pro_service.add_service.service_name_required_error') });
                return;
            }
            if (!salonIdToUse) {
                setErrors({ salon: t('pro_service.add_service.salon_required_error') });
                return;
            }
            if (!formData.price || isNaN(parseFloat(formData.price))) {
                setErrors({ price: t('pro_service.add_service.price_required_valid_error') });
                return;
            }
            if (!formData.duration || isNaN(parseInt(formData.duration))) {
                setErrors({ duration: t('pro_service.add_service.duration_required_valid_error') });
                return;
            }

            const serviceData = new FormData();
            serviceData.append('name', formData.name.trim());
            serviceData.append('salon', Number(salonIdToUse));
            serviceData.append('price', parseFloat(formData.price));
            serviceData.append('prix_fixe', String(!!formData.prix_fixe));
            serviceData.append('client_booking_disabled', String(!!formData.client_booking_disabled));
            serviceData.append('booking_confirmation_mode', formData.booking_confirmation_mode);
            serviceData.append('duration', parseInt(formData.duration));

            if (formData.description?.trim()) serviceData.append('description', formData.description.trim());

            const categoryId = formData.category || (preselectedCategory?.category_id || preselectedCategory?.id || preselectedCategory?.pk);
            if (categoryId) {
                serviceData.append('category', Number(categoryId));
            } else {
                const salonForCategories = Number(salonIdToUse);
                const sourceCategories = allCategories;

                if (preselectedCategory?.name && sourceCategories.length > 0) {
                    const matchingCategory = sourceCategories.find(cat => {
                        const catSalon = cat.salon ?? cat.salon_id;
                        return cat.name === preselectedCategory.name && Number(catSalon) === salonForCategories;
                    });
                    if (matchingCategory) {
                        const foundCategoryId = matchingCategory.category_id || matchingCategory.id || matchingCategory.pk;
                        if (foundCategoryId) serviceData.append('category', Number(foundCategoryId));
                    }
                }

                if (!serviceData.get('category') && sourceCategories.length === 1) {
                    const onlyCat = sourceCategories[0];
                    const onlyCatSalon = onlyCat.salon ?? onlyCat.salon_id;
                    if (Number(onlyCatSalon) === salonForCategories) {
                        const onlyId = onlyCat.category_id || onlyCat.id || onlyCat.pk;
                        if (onlyId) serviceData.append('category', Number(onlyId));
                    }
                }
            }

            if (preselectedCategory && !serviceData.get('category')) {
                setErrors(prev => ({ ...prev, category: t('pro_service.add_service.category_determination_error') }));
                setIsSubmitting(false);
                return;
            }

            if (images.length > 0) {
                images.forEach((image, index) => {
                    serviceData.append('images', image);
                    console.log(`Appending image ${index + 1}:`, image.name);
                });
                serviceData.append('primary_image_index', primaryImageIndex);
            } else {
                console.warn('No images to submit');
            }

            console.log('Submitting form with images:', images.map(f => f.name));
            const result = await createService({ body: serviceData }).unwrap();
            console.log('API response:', result);

            const draftValues = {
                name: formData.name.trim(),
                description: formData.description?.trim() || ''
            };
            setPendingCreatedService(result);
            setCreatedServiceDraft(draftValues);

            setFormData({
                name: '',
                description: '',
                salon: currentSalonId || '',
                category: '',
                price: '',
                prix_fixe: true,
                client_booking_disabled: false,
                booking_confirmation_mode: 'automatic',
                duration: '',
                durationDisplay: '',
                image: null,
                primaryImageIndex: 0
            });
            setImages([]);
            setImagePreviews([]);
            setErrors({});

            if (singleImageInputRef.current) singleImageInputRef.current.value = '';
            if (multipleImagesInputRef.current) multipleImagesInputRef.current.value = '';

            if (withTranslationCheck) {
                const newServiceId = result?.service_id || result?.id;
                setCreatedServiceId(newServiceId);
                setTranslationModalOpen(true);
                setTranslationModalLoading(true);
                setPendingTranslations(null);

                let attempts = 0;
                const maxAttempts = 20;
                pollIntervalRef.current = setInterval(async () => {
                    attempts++;
                    try {
                        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                        const response = await fetch(
                            `/api/services/${newServiceId}/`,
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        if (response.ok) {
                            const data = await response.json();
                            const hasTranslations = data.name_translations && Object.keys(data.name_translations).length > 0;
                            if (hasTranslations) {
                                clearInterval(pollIntervalRef.current);
                                setPendingTranslations({
                                    name: data.name_translations,
                                    description: data.description_translations || {},
                                });
                                setTranslationModalLoading(false);
                                return;
                            }
                        }
                    } catch (e) {
                        console.warn('[service translations] poll error', e);
                    }
                    if (attempts >= maxAttempts) {
                        clearInterval(pollIntervalRef.current);
                        setTranslationModalLoading(false);
                    }
                }, 1500);
            } else {
                setSnackbar({
                    open: true,
                    message: t('pro_service.add_service.service_created_success'),
                    severity: 'success'
                });
                if (onSuccess) onSuccess(result);
                if (onClose) onClose();
            }
        } catch (error) {
            console.error('AddService - Error creating service:', error);
            console.error('AddService - Error details:', error.data);

            // Show error snackbar
            setSnackbar({
                open: true,
                message: t('pro_service.add_service.creation_error'),
                severity: 'error'
            });

            if (error.data && error.data.errors) setErrors(error.data.errors);
            else if (error.data) {
                const apiErrors = {};
                if (error.data.name) apiErrors.name = error.data.name.join(' ');
                if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
                if (error.data.category) apiErrors.category = error.data.category.join(' ');
                if (error.data.description) apiErrors.description = error.data.description.join(' ');
                if (error.data.price) apiErrors.price = error.data.price.join(' ');
                if (error.data.duration) apiErrors.duration = error.data.duration.join(' ');
                if (error.data.image) apiErrors.image = error.data.image.join(' ');
                if (error.data.images) apiErrors.images = error.data.images.join(' ');
                if (error.data.primary_image_index) apiErrors.primaryImageIndex = error.data.primary_image_index.join(' ');
                if (error.data.non_field_errors) apiErrors.name = error.data.non_field_errors.join(' ');
                setErrors(apiErrors);
            } else setErrors({ general: t('pro_service.add_service.generic_creation_error') });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isLoadingData = salonsLoading || profileLoading;
    const hasSalons = salonsData && Array.isArray(salonsData) && salonsData.length > 0;

    const formContent = useMemo(() => (
        <>
            {isLoadingData && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <CircularProgress size={20} />
                    <span>{t('pro_service.add_service.loading_salons')}</span>
                </div>
            )}
            
            {!isLoadingData && !hasSalons && (
                <div style={{ 
                    padding: '16px', 
                    backgroundColor: theme.colors?.warning?.[50] || '#fff3cd', 
                    border: `1px solid ${theme.colors?.warning?.[200] || '#ffeaa7'}`, 
                    borderRadius: '8px', 
                    marginBottom: '16px', 
                    textAlign: 'center' 
                }}>
                    <p style={{ margin: 0, color: theme.colors?.warning?.[800] || '#856404' }}>
                        {t('pro_service.add_service.no_salon_found')}
                    </p>
                </div>
            )}

            <TextField
                fullWidth
                label={t('pro_service.add_service.service_name')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
                disabled={isSubmitting || isCreating || !hasSalons}
            />
            
            <TextField
                fullWidth
                label={t('pro_service.add_service.description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                margin="normal"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isSubmitting || isCreating || !hasSalons}
            />
            
            {!currentSalonId && (
                <FormControl fullWidth margin="normal" error={!!errors.salon} disabled={isSubmitting || isCreating || isLoadingData || !hasSalons}>
                    <InputLabel>{t('pro_service.add_service.salon')}</InputLabel>
                    <Select name="salon" value={formData.salon || ''} onChange={handleChange} label={t('pro_service.add_service.salon')} displayEmpty>
                        {isLoadingData ? <MenuItem disabled>{t('pro_service.add_service.loading_salons')}</MenuItem> : hasSalons ? salonsData.map(salon => {
                            const salonId = salon.salon_id || salon.id;
                            return <MenuItem key={salonId} value={Number(salonId)}>{salon.name}</MenuItem>;
                        }) : <MenuItem disabled>{t('pro_service.add_service.no_salon_available')}</MenuItem>}
                    </Select>
                    {errors.salon && <FormHelperText error>{errors.salon}</FormHelperText>}
                </FormControl>
            )}
            
            {preselectedCategory ? (
                <TextField
                    fullWidth
                    label={t('pro_service.add_service.category')}
                    value={getTranslatedField(preselectedCategory, 'name')}
                    margin="normal"
                    disabled
                    helperText={t('pro_service.add_service.preselected_category')}
                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: theme.colors.primary[700], backgroundColor: theme.colors.primary[50] } }}
                />
            ) : (
                <FormControl fullWidth margin="normal" error={!!errors.category} disabled={isSubmitting || isCreating || categoriesLoading || !hasSalons}>
                    <InputLabel>{t('pro_service.add_service.category')}</InputLabel>
                    <Select name="category" value={formData.category} onChange={handleChange} label={t('pro_service.add_service.category')} disabled={(!formData.salon && !currentSalonId) || !hasSalons}>
                        {(!formData.salon && !currentSalonId) ? <MenuItem disabled>{t('pro_service.add_service.select_salon_first')}</MenuItem> : categoriesLoading ? <MenuItem disabled>{t('pro_service.add_service.loading_categories')}</MenuItem> : allCategories && allCategories.length > 0 ? allCategories.map(category => (
                            <MenuItem key={category.category_id ?? category.id} value={category.category_id ?? category.id}>{getTranslatedField(category, 'name')}</MenuItem>
                        )) : <MenuItem disabled>{t('pro_service.add_service.no_category_available_salon')}</MenuItem>}
                    </Select>
                    {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
                </FormControl>
            )}
            
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <TextField
                    label={t('pro_service.add_service.price')}
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    margin="normal"
                    type="number"
                    inputProps={{ min: 0, step: '100' }}
                    error={!!errors.price}
                    helperText={errors.price}
                    disabled={isSubmitting || isCreating || !hasSalons}
                    sx={{ flex: 1 }}
                />
                
                <TextField
                    label={t('pro_service.add_service.duration')}
                    name="durationDisplay"
                    value={formData.durationDisplay}
                    onChange={handleChange}
                    margin="normal"
                    type="text"
                    placeholder={t('pro_service.add_service.duration_placeholder')}
                    error={!!errors.duration}
                    helperText={errors.duration || t('pro_service.add_service.duration_formats')}
                    disabled={isSubmitting || isCreating || !hasSalons}
                    sx={{ flex: 1 }}
                />
            </div>

            <FormControlLabel
                control={
                    <Switch
                        checked={!!formData.prix_fixe}
                        onChange={(e) => setFormData(prev => ({ ...prev, prix_fixe: e.target.checked }))}
                        disabled={isSubmitting || isCreating || !hasSalons}
                    />
                }
                label={t('pro_service.add_service.fixed_price')}
                sx={{ mt: 1 }}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={!!formData.client_booking_disabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, client_booking_disabled: e.target.checked }))}
                        disabled={isSubmitting || isCreating || !hasSalons}
                    />
                }
                label={t('pro_service.add_service.disable_client_booking')}
                sx={{
                    mt: 0.5,
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                        lineHeight: 1.4,
                    },
                }}
            />

            <Box sx={{ mt: 2 }}>
                <ServiceConfirmationModeField
                    value={formData.booking_confirmation_mode}
                    onChange={(value) => setFormData(prev => ({ ...prev, booking_confirmation_mode: value }))}
                    disabled={isSubmitting || isCreating || !hasSalons}
                    hidden={!!formData.client_booking_disabled}
                    labels={{
                        title: t('appointment_workflow.service_confirmation.title'),
                        automatic: t('appointment_workflow.service_confirmation.automatic'),
                        manual: t('appointment_workflow.service_confirmation.manual'),
                    }}
                />
            </Box>
            
            <div style={{ marginTop: '8px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
                    {t('pro_service.add_service.service_photos')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                    {t('pro_service.add_service.add_images_description')}
                </Typography>

                <input
                    ref={multipleImagesInputRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id={uploadInputId}
                    type="file"
                    onChange={handleMultipleImagesChange}
                    disabled={isSubmitting || isCreating || !hasSalons}
                    multiple
                />
                
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}>
                        <label htmlFor={uploadInputId}>
                            <Box
                                sx={{
                                    border: '2px dashed #e0e0e0',
                                    borderRadius: 2,
                                    height: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isSubmitting || isCreating || !hasSalons ? 'default' : 'pointer',
                                    backgroundColor: '#fafafa',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: isSubmitting || isCreating || !hasSalons ? '#e0e0e0' : theme.colors.primary[500],
                                        backgroundColor: isSubmitting || isCreating || !hasSalons ? '#fafafa' : '#f5f5f5'
                                    }
                                }}
                                component="div"
                            >
                                <AddIcon sx={{ fontSize: 32, color: '#999', mb: 1 }} />
                                <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', fontSize: '0.85rem', fontWeight: 500 }}>
                                    {t('pro_service.add_service.add_photo')}
                                </Typography>
                            </Box>
                        </label>
                    </Grid>

                    {imagePreviews.map((preview, index) => (
                        <Grid item xs={6} sm={4} md={3} key={`image-${index}`}>
                            <Box sx={{ position: 'relative', height: 120, borderRadius: 2, overflow: 'hidden', border: primaryImageIndex === index ? `2px solid ${theme.colors.primary[500]}` : '1px solid #e0e0e0' }}>
                                <img 
                                    src={preview} 
                                    alt={t('pro_service.add_service.image_alt', { index: index + 1 })} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '2px' }} 
                                    onLoad={() => console.log(`Image ${index + 1} loaded successfully`)}
                                    onError={(e) => console.error(`Image ${index + 1} failed to load:`, e, preview)}
                                />
                                <IconButton
                                    size="small"
                                    sx={{ position: 'absolute', bottom: 4, right: 4, backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', padding: '4px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
                                    onClick={() => setPrimaryImage(index)}
                                    disabled={isSubmitting || isCreating}
                                >
                                    {index === primaryImageIndex ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                                </IconButton>
                                <IconButton 
                                    size="small" 
                                    sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', padding: '4px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
                                    onClick={() => removeImage(index)}
                                    disabled={isSubmitting || isCreating}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                                {index === primaryImageIndex && (
                                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, backgroundColor: 'rgba(25, 118, 210, 0.9)', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '0 4px 0 0', fontWeight: 500 }}>
                                        {t('pro_service.add_service.primary')}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {imagePreviews.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CameraAltIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="caption" sx={{ color: '#666' }}>
                            {t('pro_service.add_service.photos_added', { count: imagePreviews.length })}
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddPhotoAlternateIcon />}
                        component="label"
                        htmlFor={uploadInputId}
                        disabled={isSubmitting || isCreating || !hasSalons}
                        sx={{ borderColor: '#e0e0e0', color: '#666', '&:hover': { borderColor: theme.colors.primary[500], backgroundColor: 'rgba(0,0,0,0.02)' } }}
                    >
                        {t('pro_service.add_service.add_photos')}
                    </Button>
                </Box>

                {errors.images && <FormHelperText error sx={{ mt: 1 }}>{errors.images}</FormHelperText>}
            </div>
        </>
    ), [
        isLoadingData, hasSalons, formData, errors, salonsData, currentSalonId, preselectedCategory,
        allCategories, categoriesLoading, isSubmitting, isCreating, imagePreviews, primaryImageIndex
    ]);

    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;
    const isDisabled = isSubmitting || isCreating || !hasSalons;

    return (
        <>
            <MuiDialog
                open={open}
                triggerButton={triggerButton}
                onClose={dialogClosed}
                onExternalClose={onClose}
                maxWidth="sm"
                nested={nested}
                sx={sx}
                {...otherProps}
            >
                <DialogTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={IconContainer(theme.colors)}>
                            <SpaIcon sx={{ fontSize: '30px' }} />
                        </div>
                        <div>
                            <Typography component="span" variant="h6" sx={DialogTitleText(theme.colors)}>
                                {t('pro_service.add_service.add_service_title')}
                            </Typography>
                            <Typography sx={{ ...SubTitleScreen(theme.colors), fontSize: 15 }}>
                                {t('pro_service.add_service.add_service_subtitle')}
                            </Typography>
                        </div>
                    </div>
                </DialogTitle>
                <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
                    {formContent}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', px: 2, py: 1.5 }}>
                    <Button
                        onClick={() => { if (onClose) onClose(); }}
                        sx={SecondaryButton}
                    >
                        {t('common.close')}
                    </Button>
                    <Button
                        onClick={() => handleSubmit(false)}
                        sx={PrimaryButton}
                        disabled={isDisabled}
                        startIcon={isDisabled ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    >
                        {isSubmitting || isCreating ? t('pro_dashboard.salon.saving') : t('pro_service.add_service.create')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => handleSubmit(true)}
                        disabled={isDisabled}
                        startIcon={<TranslateIcon fontSize="small" />}
                        sx={{
                            borderColor: theme.colors.primary[400],
                            color: theme.colors.primary[700],
                            fontSize: '0.75rem',
                            padding: '6px 10px',
                            '&:hover': { borderColor: theme.colors.primary[600], backgroundColor: theme.colors.primary[50] }
                        }}
                    >
                        {t('pro_salon.translations.save_and_check', 'Enregistrer et vérifier la traduction')}
                    </Button>
                </DialogActions>
            </MuiDialog>

            <TranslationValidationModal
                open={translationModalOpen}
                onClose={handleTranslationSkip}
                onValidate={handleSaveTranslations}
                salonName={createdServiceDraft.name}
                salonDescription={createdServiceDraft.description}
                isLoading={translationModalLoading}
                translations={pendingTranslations}
            />
            
            {/* Snackbar for success/error messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
       </>
    );
};

export default AddService;
